import { useEffect, useRef, useState } from "react";

/**
 * The branded threshold from design.md section 4.
 *
 * The timing is the specification, not a preference: two panels stand still for 500–550 ms with the
 * wordmark legible, then travel apart over about 900 ms, for a total of roughly 1.4–1.5 s. The
 * overview is already rendered underneath before the panels appear, so removing them reveals a
 * finished screen rather than starting one.
 *
 * With `prefers-reduced-motion` the panels never appear at all. The reduced path shows the completed
 * dashboard immediately, with nothing left hidden or transparent behind it.
 */

const STILL_MS = 520;
const TRAVEL_MS = 900;
const FALLBACK_MS = STILL_MS + TRAVEL_MS + 400;

export function PortalOpening({ onDone }: { onDone: () => void }) {
  const [opening, setOpening] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const finished = useRef(false);

  useEffect(() => {
    const finish = () => {
      if (finished.current) {
        return;
      }
      finished.current = true;
      onDone();
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finish();
      return;
    }

    let startTimer = 0;
    let fallbackTimer = 0;

    // Two frames, so the overview beneath is laid out and painted before the panels are shown.
    const firstFrame = requestAnimationFrame(() => {
      const secondFrame = requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        startTimer = window.setTimeout(() => setOpening(true), STILL_MS);
        fallbackTimer = window.setTimeout(finish, FALLBACK_MS);
      });
      startTimer = secondFrame;
    });

    // Any deliberate input dismisses it at once.
    window.addEventListener("pointerdown", finish, { once: true });
    window.addEventListener("keydown", finish, { once: true });
    window.addEventListener("touchstart", finish, { once: true });

    return () => {
      cancelAnimationFrame(firstFrame);
      window.clearTimeout(startTimer);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener("pointerdown", finish);
      window.removeEventListener("keydown", finish);
      window.removeEventListener("touchstart", finish);
    };
  }, [onDone]);

  return (
    <div
      className={`portal-threshold${opening ? " is-opening" : ""}`}
      aria-hidden="true"
      data-testid="portal-opening"
    >
      <div
        className="threshold-panel threshold-left"
        ref={panelRef}
        onTransitionEnd={(event) => {
          if (event.propertyName === "transform" && !finished.current) {
            finished.current = true;
            onDone();
          }
        }}
      >
        <div className="threshold-grid" />
      </div>
      <div className="threshold-panel threshold-right">
        <div className="threshold-grid" />
      </div>
      <div className="threshold-seam" />
      <div className="threshold-wordmark">
        <span>PETROVIC SYSTEMS</span>
        <small>Kundportal</small>
      </div>
    </div>
  );
}
